import { publicAgentsRoutes } from '@rms/agents';
import { authGuard, embedAuthGuard, iframeAuthGuard } from '@rms/auth';
import { whiteLabelResolver } from '@rms/core/layout';
import { agentOrganizationResolver, embeddedAppsResolver } from '@rms/org';
import { subscriptionResolver } from '@rms/subscription';
import { disableSupportChatResolver } from '@rms/support-chat';
import { VexRoutes } from '@vex/interfaces/vex-route.interface';

import { LanguageParamResolver } from './core/language-detection/lang-param.resolver';
import { billingResolver } from './domains/billing/resolver';
import { LayoutComponent } from './layouts/layout/layout.component';

export const appRoutes: VexRoutes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/auth/login').then((m) => m.LoginComponent),
  },
  {
    path: 'autologin',
    loadComponent: () => import('./pages/auth/autologin').then((m) => m.AutologinComponent),
    resolve: [LanguageParamResolver, whiteLabelResolver],
  },
  {
    path: 'public-embed',
    loadComponent: () => import('./pages/auth/public-embed').then((m) => m.PublicEmbedComponent),
    resolve: [LanguageParamResolver, whiteLabelResolver],
    canActivate: [embedAuthGuard],
  },
  {
    path: 'register',
    loadComponent: () => import('./pages/auth/register').then((m) => m.RegisterComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/auth/forgot-password').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/auth/reset-password').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'set-password',
    loadComponent: () => import('./pages/auth/set-password').then((m) => m.SetPasswordComponent),
  },
  {
    path: 'coming-soon',
    loadComponent: () => import('./pages/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
  },
  {
    path: 'oauth-result',
    loadComponent: () => import('./pages/oauth-result/oauth-result.component').then((m) => m.OauthResultComponent),
  },
  {
    path: 'error-page',
    loadComponent: () => import('./pages/error-page/error-page.component').then((m) => m.ErrorPageComponent),
  },
  {
    path: 'public-agents',
    loadComponent: () => import('./pages/public-agents/public-agents.component').then((m) => m.PublicAgentsComponent),
    resolve: [
      LanguageParamResolver,
      subscriptionResolver,
      agentOrganizationResolver,
      whiteLabelResolver,
      billingResolver,
    ],
    canActivate: [iframeAuthGuard],
    children: publicAgentsRoutes,
  },
  {
    path: 'public-billing',
    loadComponent: () =>
      import('./pages/public-billing/public-billing.component').then((m) => m.PublicBillingComponent),
    resolve: [LanguageParamResolver, subscriptionResolver, whiteLabelResolver, billingResolver],
    canActivate: [iframeAuthGuard],
  },
  {
    path: 'public-calls',
    loadComponent: () => import('./pages/public-calls').then((m) => m.PublicCallsComponent),
    resolve: [LanguageParamResolver, subscriptionResolver, whiteLabelResolver, disableSupportChatResolver],
    canActivate: [embedAuthGuard],
  },
  {
    path: 'public-crm',
    loadComponent: () => import('./pages/auth/public-crm').then((m) => m.PublicCrmComponent),
    resolve: [LanguageParamResolver, whiteLabelResolver],
  },
  {
    path: 'csp',
    loadComponent: () => import('./pages/csp/csp-main/csp-main.component').then((m) => m.CspMainComponent),
    loadChildren: () => import('./pages/csp/csp.routing').then((m) => m.cspRoutes),
    resolve: [LanguageParamResolver, subscriptionResolver, disableSupportChatResolver],
  },
  {
    path: '',
    component: LayoutComponent,
    canActivateChild: [authGuard],
    resolve: {
      subscription: subscriptionResolver,
      embeddedApps: embeddedAppsResolver,
      agentOrganization: agentOrganizationResolver,
      billingResolver: billingResolver,
    },
    children: [
      {
        path: 'inbox',
        loadComponent: () => import('./pages/inbox').then((m) => m.InboxComponent),
      },
      {
        path: 'sms',
        loadChildren: () => import('./pages/sms').then((m) => m.smsRoutes),
      },
      {
        path: 'agents',
        loadComponent: () => import('./pages/agents/agents.component').then((m) => m.AgentsComponent),
        // TODO: uncomment before release DEV-1165
        // canActivate: [firstLoginGuard],
        children: publicAgentsRoutes,
      },
      {
        path: 'call-campaigns',
        loadComponent: () => import('./pages/call-campaigns').then((m) => m.RootCallCampaignsComponent),
        loadChildren: () => import('./pages/call-campaigns').then((m) => m.callCampaignsRoutes),
      },
      {
        path: 'knowledge-base',
        loadComponent: () => import('./pages/knowledge-base').then((m) => m.KnowledgeBaseMainComponent),
        loadChildren: () => import('./pages/knowledge-base').then((m) => m.knowledgeBaseRoutes),
      },
      {
        path: 'phone-system',
        loadComponent: () => import('./pages/phone-system').then((m) => m.PhoneSystemMainComponent),
        loadChildren: () => import('./pages/phone-system').then((m) => m.phoneSystemRoutes),
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
      },
      {
        path: 'shop',
        loadComponent: () => import('./pages/shop').then((m) => m.ShopComponent),
      },
      {
        path: 'integrations',
        loadComponent: () => import('./pages/integrations/integrations.component').then((m) => m.IntegrationsComponent),
        loadChildren: () => import('./pages/integrations/integrations.routes').then((m) => m.routes),
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/settings/settings.component').then((m) => m.SettingsComponent),
        loadChildren: () => import('./pages/settings/settings.routes').then((m) => m.routes),
      },
      {
        path: 'team',
        loadComponent: () => import('./pages/team/team.component').then((m) => m.TeamComponent),
        loadChildren: () => import('./pages/team/team.routes').then((m) => m.routes),
      },
      {
        path: 'webhooks',
        loadComponent: () => import('./pages/webhooks/webhooks.component').then((m) => m.WebhooksComponent),
        loadChildren: () => import('./pages/webhooks/webhooks.routes').then((m) => m.routes),
      },
      {
        path: 'widgets',
        loadComponent: () => import('./pages/widgets/widgets.component').then((m) => m.WidgetsComponent),
        loadChildren: () => import('./pages/widgets/widgets.routes').then((m) => m.routes),
      },
      {
        path: 'contacts',
        loadComponent: () => import('./pages/contacts/contacts.component').then((m) => m.ContactsComponent),
        loadChildren: () => import('./pages/contacts/contacts.routing').then((m) => m.contactsRoutes),
      },
      {
        path: 'calls',
        loadComponent: () => import('./pages/calls/calls.component').then((m) => m.CallsComponent),
        loadChildren: () => import('./pages/calls/calls.routing').then((m) => m.callsRoutes),
      },
      {
        path: 'call-analytics',
        loadComponent: () =>
          import('./pages/call-analytics/call-analytics.component').then((m) => m.CallAnalyticsComponent),
      },
      {
        path: 'preferences',
        loadComponent: () => import('./pages/preferences/preferences.component').then((m) => m.PreferencesComponent),
      },
      {
        path: 'call-flow',
        loadComponent: () =>
          import('./domains/pbx/call-flow-builder').then((m) => m.CallFlowBuilderEditorDemoComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./domains/pbx/call-flow-builder').then((m) => m.CallFlowBuilderEditorComponent),
          },
        ],
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },
];
